import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';

type KeyboardState = {
    keyboardInfo: KeyboardInfo | undefined;
    keyboardStatus: 'willShow' | 'visible' | 'hidden';
};

const fakeHeightListeners: Set<(height: number | undefined) => unknown> = new Set();
export function setFakeKeyboardHeight(height: number | undefined) {
    fakeHeightListeners.forEach(listener => listener(height));
}

export function useKeyboardScrollEffect(elementRef: RefObject<HTMLElement>) {
    const { keyboardStatus } = useKeyboardInfo();

    useEffect(() => {
        if (keyboardStatus) {
            elementRef.current?.scrollIntoView({ inline: 'start', behavior: 'smooth' });
        }
    }, [ elementRef, keyboardStatus ]);
}

export function useKeyboardInfo() {
    const [ keyboardInfo, setKeyboardInfo ] = useState<KeyboardState>({
        keyboardStatus: 'hidden',
        keyboardInfo: undefined,
    });
    const lastStatusRef = useRef<KeyboardState['keyboardStatus']>('hidden');

    const setKeyboardInfoDeduped = useCallback((next: KeyboardState) => {
        if (next.keyboardStatus === lastStatusRef.current) return;
        lastStatusRef.current = next.keyboardStatus;
        setKeyboardInfo(next);
    }, []);

    useEffect(() => {
        const fakeHeightListener = (height: number | undefined) => {
            if ((height ?? 0) > 0) {
                setKeyboardInfoDeduped({ keyboardInfo: { keyboardHeight: height ?? 0 }, keyboardStatus: 'visible' });
            } else {
                setKeyboardInfoDeduped({ keyboardInfo: undefined, keyboardStatus: 'hidden' });
            }
        };
        fakeHeightListeners.add(fakeHeightListener);

        if (Capacitor.isNativePlatform()) {
            const handles: PluginListenerHandle[] = [];
            Keyboard.addListener('keyboardWillShow', info => {
                setKeyboardInfoDeduped({ keyboardInfo: info, keyboardStatus: 'willShow' });
            }).then(handle => handles.push(handle));
            Keyboard.addListener('keyboardDidShow', info => {
                setKeyboardInfoDeduped({ keyboardInfo: info, keyboardStatus: 'visible' });
            }).then(handle => handles.push(handle));
            Keyboard.addListener('keyboardWillHide', () => {
                setKeyboardInfoDeduped({ keyboardInfo: undefined, keyboardStatus: 'hidden' });
            }).then(handle => handles.push(handle));
            Keyboard.addListener('keyboardDidHide', () => {
                setKeyboardInfoDeduped({ keyboardInfo: undefined, keyboardStatus: 'hidden' });
            }).then(handle => handles.push(handle));
            return () => {
                handles.forEach((h) => h.remove());
                fakeHeightListeners.delete(fakeHeightListener);
            };
        } else {
            return () => {
                fakeHeightListeners.delete(fakeHeightListener);
            };
        }
    }, [ setKeyboardInfoDeduped ]);

    return keyboardInfo;
}
